import { useCallback } from 'react'
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { useSelector } from 'react-redux'
import { ContentStateProperty } from '../../../../@types/types'
import { ScreenName } from '../../../../services/domain/wizardScreens'
import { InitState } from '../../../../redux/reducers/root/initialState'
import {
  increaseSubmit,
  resetTouched as resetTouchedAction,
  setScrolled as setScrolledAction,
  setTouched as setTouchedAction,
} from '../../../../redux/reducers/formState/formStateFeature'
import { useGetFinalParsedValidationSchema } from '../../../../services/validation/validationService'
import { ValidationSchemaParsed } from '../../../../@types/validation-types'
import { FormStateFeatureState } from '../../../../@types/state-types'
import { useAppDispatch } from '../../../../app/hooks'

export type FormContextType = {
  formState: FormStateFeatureState
  increaseSubmitCount: () => void
  setTouched: (propertyName: ContentStateProperty) => void
  resetTouched: (propertyName: ContentStateProperty[]) => void
  setScrolled: (screenName: ScreenName) => void
}

function getPropertyScreenFromValidationSchema(
  validationSchema: ValidationSchemaParsed,
  parameterName: ContentStateProperty
) {
  return Object.entries(validationSchema).find(([_screen, schema]) =>
    Object.keys(schema).includes(parameterName)
  )?.[0] as ScreenName | undefined
}

function isNotNullable<TValue>(value: TValue | undefined | null): value is TValue {
  return value !== null && value !== undefined
}

export const useFormState = () => {
  const formState = useSelector<InitState, FormStateFeatureState>((state) => state.formStateFeature)
  const validationSchema = useGetFinalParsedValidationSchema()
  const dispatch = useAppDispatch()
  const increaseSubmitCount = useCallback(() => {
    dispatch(increaseSubmit())
  }, [dispatch])

  const setTouched = useCallback(
    (parameterName: ContentStateProperty) => {
      // If the validation schema does not have an entry for a property, the screen will be undefined. Means: the solution is not completely true,
      // but it fulfills the purpose
      const screen = getPropertyScreenFromValidationSchema(validationSchema, parameterName)
      if (!formState.touched[parameterName]) {
        dispatch(setTouchedAction({ property: parameterName, screen: screen }))
      }
    },
    [dispatch, formState.touched, validationSchema]
  )
  const setScrolled = useCallback(
    (screenName: ScreenName) => {
      if (!formState.scrolled[screenName]) {
        dispatch(setScrolledAction({ screen: screenName }))
      }
    },
    [dispatch, formState.scrolled]
  )
  const resetTouched = useCallback(
    (properties: ContentStateProperty[]) => {
      dispatch(
        resetTouchedAction({
          properties: properties,
          screens: properties
            .map((property) => getPropertyScreenFromValidationSchema(validationSchema, property))
            .filter(isNotNullable),
        })
      )
    },
    [dispatch, validationSchema]
  )
  return {
    formState,
    increaseSubmitCount,
    setTouched,
    setScrolled,
    resetTouched,
  } as FormContextType
}
